import React, { useCallback, useEffect } from 'react';
import { IoArrowBackOutline } from 'react-icons/io5';

import { useLocation } from '@reach/router';
import { graphql, navigate } from 'gatsby';

import DescriptionContent from '../components/DescriptionContent';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import ShareIcon from '../components/ShareIcons/ShareIcon';
import Header from '../components/navbar/Header';
import MobileNav from '../components/navbar/MobileNav';
import { CtaEntry } from '../components/web/cta';
import { useMixpanel } from '../functions/Mixpanel';

import * as style from './Announcement.module.css';

const Announcement = (props) => {
  const { mixpanel } = useMixpanel();
  const { data } = props;
  const annData = data.contentfulCompanyAnnouncements;
  const fullPost = annData.fullAnnouncement.childMarkdownRemark;
  const reference = annData?.references?.childMarkdownRemark;
  const referenceLength = reference?.htmlAst.children[0].children.length;
  const annTitle = annData.titles;
  const genericData = data.allContentfulAlifeGenericContent.edges[0].node;
  const cta = annData.cta;

  useEffect(() => {
    mixpanel.track('View Announcement', { 'Announcement Title': annTitle });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shareAnnouncement = useCallback(
    (title, method) => {
      mixpanel.track('Share Announcement', {
        title: title,
        'Share Method': method,
      });
    },
    [mixpanel],
  );

  const location = useLocation();

  return (
    <div>
      <Header />
      <MobileNav />
      <Seo
        title={annData.titles}
        description={annData.description}
        siteUrl={`https://www.alifehealth.com/press/${annData.slug}/`}
      />
      <div className="tw-flex tw-flex-col tw-max-w-[1128px] tw-pt-[32px] md:tw-pt-[80px] md:tw-px-[16px] lg:tw-pt-[100px] tw-m-auto tw-items-center tw-gap-[16px]">
        <div className={`tailwind-preflight ${style.ANContainer}`}>
          <div
            aria-hidden="true"
            onClick={() => navigate(-1)}
            className={style.ANBackArrow}
          >
            <a className={style.ANBack} href="/press">
              <IoArrowBackOutline />
              <p className={style.ANBackText}>PRESS</p>
            </a>
          </div>
          <h1> {annData.titles}</h1>
          <div>
            <p className={style.AnnDescription}>{annData.description}</p>
          </div>
          <div className={style.shareDiv}>
            <p>
              {annData.location} /{annData.date}
            </p>

            <div className={style.socialMediaIcons}>
              <ShareIcon
                url={location.href}
                description={annData.description}
                subject={annData.description}
                titleHeading={annData.description}
                functionName={shareAnnouncement}
                position="top"
              />
            </div>
          </div>

          <DescriptionContent
            className={`contentfulLists ${style.ANList}`}
            {...fullPost}
          />
        </div>

        {cta && (
          <div className="tw-align-center">
            <CtaEntry
              className="tw-mt-[32px] tw-mb-[32px]"
              title={cta.title}
              body={cta.subheaderText}
              imageData={cta.image.gatsbyImageData}
              buttonText={cta.buttonText}
              onClick={() => {
                mixpanel.track('Announcement CTA Click', {
                  Title: cta.title,
                  Body: cta.subheaderText,
                  ButtonText: cta.buttonText,
                  Link: cta.link,
                  UpdatedAt: cta.updatedAt,
                  CtaDescriptionForTracking: cta.ctaDescriptionForTracking,
                });
              }}
              link={cta.link}
              primaryColorDark={cta.primaryColorDark.value}
              primaryColorLight={cta.primaryColorLight.value}
              bottomPadding
            />
          </div>
        )}
        <div className={`tailwind-preflight ${style.ANContainer}`}>
          <div>
            <h1>About Alife Health</h1>
            <p>{genericData.aboutAlife.aboutAlife}</p>

            <div className={style.pressContact}>
              <p>
                <span className="subTitle">PRESS CONTACT: </span>
                {genericData.pressContactName},{' '}
                <a
                  href="mailto:press@alifehealth.com"
                  onClick={() => mixpanel.track('Make Press Inquiry')}
                >
                  {genericData.pressContactEmail}
                </a>
                , {genericData.pressContactNumber}
              </p>
            </div>
          </div>
          {reference !== undefined && referenceLength > 1 && (
            <div className={style.references}>
              <h1>References</h1>
              <ul className="contentfulLists">
                <DescriptionContent
                  {...reference}
                  className={style.references}
                />
              </ul>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Announcement;

export const AnnouncementQuery = graphql`
  query AnnouncementQuery($slug: String!) {
    contentfulCompanyAnnouncements(slug: { eq: $slug }) {
      titles
      date(formatString: "MMMM Do, YYYY")
      description
      location
      fullAnnouncement {
        childMarkdownRemark {
          htmlAst
        }
      }
      slug
      cta {
        contentful_id
        title
        subheaderText
        image {
          gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
        }
        link
        buttonText
        updatedAt(formatString: "MMMM D, YYYY")
        ctaDescriptionForTracking
        primaryColorDark {
          value
        }
        primaryColorLight {
          value
        }
        __typename
      }
      references {
        childMarkdownRemark {
          htmlAst
        }
      }
    }
    allContentfulAlifeGenericContent {
      edges {
        node {
          aboutAlife {
            aboutAlife
          }
          pressContactEmail
          pressContactName
          pressContactNumber
        }
      }
    }
  }
`;
